import { useMemo } from "react";
import { fetchCacheInsights } from "../api";
import { usePolledData } from "../hooks/usePolledData";
import { fmtDate, fmtMs, fmtNum } from "../format";
import { Section } from "../components/Section";
import { DataTable, type ColumnDef } from "../components/DataTable";
import { ApiTypeTag } from "../components/Badges";
import type {
  CacheCountRow,
  CacheResponse,
  HotPanRow,
  RecentMissRow,
} from "../types";

interface Props {
  registerState: (state: {
    loading: boolean;
    lastUpdated: Date | null;
    refetch: () => void;
  }) => void;
}

export const CachePage = ({ registerState }: Props) => {
  const { data, error, loading, lastUpdated, refetch } = usePolledData<CacheResponse>(
    () => fetchCacheInsights(),
    { intervalMs: 20_000 },
  );
  registerState({ loading, lastUpdated, refetch });

  const countsCols = useMemo<ColumnDef<CacheCountRow>[]>(
    () => [
      {
        key: "api",
        header: "API",
        render: (r) => <ApiTypeTag type={r._id.api_type} />,
      },
      { key: "brand", header: "Brand", render: (r) => r._id.brand_id || "—" },
      { key: "docs", header: "Docs", align: "right", render: (r) => fmtNum(r.docs) },
      {
        key: "total_hits",
        header: "Total Hits",
        align: "right",
        render: (r) => fmtNum(r.total_hits),
      },
      {
        key: "avg_hits",
        header: "Avg Hits / Doc",
        align: "right",
        render: (r) => (r.avg_hits ? r.avg_hits.toFixed(2) : "0.00"),
      },
      {
        key: "ttl",
        header: "Avg TTL (d)",
        align: "right",
        render: (r) => (r.avg_ttl_days ? r.avg_ttl_days.toFixed(1) : "0"),
      },
    ],
    [],
  );

  const hotCols = useMemo<ColumnDef<HotPanRow>[]>(
    () => [
      { key: "pan", header: "PAN", render: (r) => r.pan || "—" },
      { key: "api", header: "API", render: (r) => <ApiTypeTag type={r.api_type} /> },
      { key: "brand", header: "Brand", render: (r) => r.brand_id || "—" },
      {
        key: "provider",
        header: "Provider",
        render: (r) => r.provider || "—",
      },
      {
        key: "hits",
        header: "Hits",
        align: "right",
        render: (r) => fmtNum(r.hit_count),
      },
      {
        key: "ttl",
        header: "TTL",
        align: "right",
        render: (r) => `${r.ttl_days}d`,
      },
      {
        key: "expires",
        header: "Expires",
        render: (r) => fmtDate(r.expires_at),
      },
      {
        key: "last",
        header: "Last access",
        render: (r) => fmtDate(r.last_accessed_at),
      },
    ],
    [],
  );

  const missCols = useMemo<ColumnDef<RecentMissRow>[]>(
    () => [
      { key: "time", header: "Time", render: (r) => fmtDate(r.created_at) },
      { key: "brand", header: "Brand", render: (r) => r.brand_id || "—" },
      { key: "api", header: "API", render: (r) => <ApiTypeTag type={r.api_type} /> },
      { key: "pan", header: "PAN", render: (r) => r.pan || "—" },
      {
        key: "provider",
        header: "Upstream",
        render: (r) => r.upstream_provider || "—",
      },
      {
        key: "dur",
        header: "Time",
        align: "right",
        render: (r) => fmtMs(r.response_time_ms),
      },
    ],
    [],
  );

  if (error) {
    return (
      <div className="error-banner">
        <strong>Failed to load cache insights:</strong> {error.message}
      </div>
    );
  }

  const totalDocs = (data?.counts_by_api_brand || []).reduce(
    (n, r) => n + r.docs,
    0,
  );
  const totalHits = (data?.counts_by_api_brand || []).reduce(
    (n, r) => n + (r.total_hits || 0),
    0,
  );

  return (
    <>
      <Section
        title="api_cache density"
        description="Rows per (api_type, brand_id) pair."
        right={
          <div className="dim small">
            {fmtNum(totalDocs)} docs · {fmtNum(totalHits)} cumulative hits
          </div>
        }
      >
        <DataTable
          columns={countsCols}
          rows={data?.counts_by_api_brand}
          rowKey={(r) => `${r._id.api_type}|${r._id.brand_id}`}
          loading={loading}
        />
      </Section>

      <Section title="Hot PANs" description="Top 20 by hit_count.">
        <DataTable
          columns={hotCols}
          rows={data?.hot_pans}
          rowKey={(r) => r._id}
          loading={loading}
        />
      </Section>

      <Section title="Recent cache misses (last 20)">
        <DataTable
          columns={missCols}
          rows={data?.recent_cache_misses}
          rowKey={(r) => r._id}
          loading={loading}
        />
      </Section>
    </>
  );
};
